/**
 * OAUTH — tarayıcıdan doğrudan, sunucusuz.
 *
 * "Sunucu hiçbir şey tutmaz" kararının (27.09.2026) teknik karşılığı: bulut
 * yetkilendirmesinin HİÇBİR adımı Parafon sunucusundan geçmez. Açılır pencere
 * sağlayıcıya gider, sağlayıcı `/oauth-donus.html`e döner, o sayfa sonucu
 * `BroadcastChannel` ile uygulamaya iletir ve kapanır. Kod değişimi (PKCE)
 * tarayıcıdan sağlayıcının belirteç uç noktasına doğrudan yapılır.
 *
 * NİÇİN `window.opener` DEĞİL `BroadcastChannel`: uygulama sayfası
 * `Cross-Origin-Opener-Policy: same-origin` ile sunulduğunda (geliştirme
 * sunucusu bunu gönderiyor) açılır pencere başka bir kökene gidince opener
 * bağı KOPAR ve geri dönse de düzelmez. Aynı köken + aynı depolama bölümü
 * olduğu sürece `BroadcastChannel` her iki durumda da çalışıyor.
 *
 * İSTEMCİ KİMLİKLERİ gizli değildir (PKCE ve belirteç akışı istemci sırrı
 * kullanmıyor); derleme anında `VITE_PARAFON_*_ISTEMCI` ortam
 * değişkenlerinden gelir. Tanımlı olmayan sağlayıcı arayüzde "bu sunucuda
 * yapılandırılmamış" diye görünür — gizlenmez, uydurulmaz.
 */

import { M } from "../dil";
import { merkezKasaIstegi, merkezTabanAdresi } from "../central/createCentral";
import type { KasaIstegi } from "./hedefler/parafonSunucu";

export interface BulutIstemcileri {
  readonly dropbox: string | null;
  readonly google: string | null;
  readonly onedrive: string | null;
  /**
   * "Parafon sunucusu" hedefinin istek yolu (merkez taşıyıcısı). `null` ya da
   * verilmemiş = bu oturumda merkez yok (`?merkez=kapali`, geliştirme sahtesi).
   */
  readonly merkez?: KasaIstegi | null;
}

function ortam(ad: string): string | null {
  const env = (import.meta as unknown as { env?: Record<string, unknown> }).env ?? {};
  const v = env[ad];
  return typeof v === "string" && v.trim().length > 0 ? v.trim() : null;
}

export function bulutIstemcileri(): BulutIstemcileri {
  const taban = merkezTabanAdresi();
  return {
    dropbox: ortam("VITE_PARAFON_DROPBOX_ISTEMCI"),
    google: ortam("VITE_PARAFON_GOOGLE_ISTEMCI"),
    onedrive: ortam("VITE_PARAFON_ONEDRIVE_ISTEMCI"),
    merkez: taban === null ? null : merkezKasaIstegi(taban),
  };
}

export const DONUS_YOLU = "/oauth-donus.html";
export const OAUTH_KANALI = "parafon-oauth";

export function donusAdresi(): string {
  return `${globalThis.location.origin}${DONUS_YOLU}`;
}

function base64url(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

export function rastgele(bayt = 32): string {
  const b = new Uint8Array(bayt);
  globalThis.crypto.getRandomValues(b);
  return base64url(b);
}

export interface Pkce {
  readonly dogrulayici: string;
  readonly meydanOkuma: string;
}

/** RFC 7636, S256. */
export async function pkceUret(): Promise<Pkce> {
  const dogrulayici = rastgele(48);
  const ozet = await globalThis.crypto.subtle.digest(
    "SHA-256",
    new TextEncoder().encode(dogrulayici),
  );
  return { dogrulayici, meydanOkuma: base64url(new Uint8Array(ozet)) };
}

/** Dönüş sayfasının kanala bıraktığı ileti. */
export interface DonusIletisi {
  readonly state: string | null;
  /** Sorgu dizesi ve parça (#) birleştirilmiş parametreler. */
  readonly params: string;
}

export interface Yetkilendirme {
  /**
   * Açılır pencereyi AÇAR — kullanıcı hareketinin içinden, `await`ten ÖNCE
   * çağrılmalı (tarayıcı gecikmiş açılır pencereyi engeller). Pencere önce
   * boş açılır, adres hazır olunca oraya gider.
   */
  (adres: () => Promise<string>, state: string, sinyal?: AbortSignal): Promise<URLSearchParams>;
}

const ZAMAN_ASIMI_MS = 5 * 60_000;

export const acilirPencereIleYetkilendir: Yetkilendirme = (adres, state, sinyal) => {
  const pencere = globalThis.open("about:blank", "parafon-oauth", "popup,width=520,height=720");
  if (pencere === null) {
    return Promise.reject(
      new Error(M.yedek.oauth.pencereEngellendi),
    );
  }
  return new Promise<URLSearchParams>((resolve, reject) => {
    const kanal = new BroadcastChannel(OAUTH_KANALI);
    const bitir = (): void => {
      clearTimeout(zaman);
      kanal.close();
      sinyal?.removeEventListener("abort", iptal);
    };
    const iptal = (): void => {
      bitir();
      try {
        pencere.close();
      } catch {
        // kopmuş pencere — önemsiz
      }
      reject(new Error(M.yedek.oauth.iptal));
    };
    const zaman = setTimeout(() => {
      bitir();
      reject(new Error(M.yedek.oauth.zamanAsimi));
    }, ZAMAN_ASIMI_MS);
    sinyal?.addEventListener("abort", iptal);
    kanal.onmessage = (ev: MessageEvent<DonusIletisi>) => {
      const ileti = ev.data;
      if (ileti === null || typeof ileti !== "object" || ileti.state !== state) return;
      bitir();
      const p = new URLSearchParams(ileti.params);
      const hata = p.get("error");
      if (hata !== null) {
        reject(new Error(M.yedek.oauth.yetkiYok(p.get("error_description") ?? hata)));
        return;
      }
      resolve(p);
    };
    adres().then(
      (url) => {
        pencere.location.href = url;
      },
      (e: unknown) => {
        bitir();
        pencere.close();
        reject(e);
      },
    );
  });
};

/** Bulut hedeflerinin dış bağımlılıkları — testler sahtesini geçirir. */
export interface BulutBagimliliklari {
  readonly fetch: typeof fetch;
  readonly yetkilendir: Yetkilendirme;
  readonly saat: () => Date;
}

/** Belirteç uç noktasına form gönderimi; hata gövdesini mesaja katar. */
export async function formGonder(
  fetchFn: typeof fetch,
  url: string,
  alanlar: Record<string, string>,
): Promise<Record<string, unknown>> {
  const yanit = await fetchFn(url, {
    method: "POST",
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
    body: new URLSearchParams(alanlar).toString(),
  });
  const govde = (await yanit.json().catch(() => ({}))) as Record<string, unknown>;
  if (!yanit.ok) {
    const e = new BelirtecHatasi(
      yanit.status,
      String(govde["error"] ?? ""),
      M.yedek.oauth.belirtecYok(yanit.status, String(govde["error_description"] ?? govde["error"] ?? "")),
    );
    throw e;
  }
  return govde;
}

export class BelirtecHatasi extends Error {
  constructor(
    readonly durum: number,
    readonly kod: string,
    mesaj: string,
  ) {
    super(mesaj);
    this.name = "BelirtecHatasi";
  }
}

/** Kısa bir güvenlik payıyla: bitmesine 60 sn kalan anahtar bitmiş sayılır. */
export function gecerliMi(bitis: string | null | undefined, simdi: Date): boolean {
  return bitis !== null && bitis !== undefined && Date.parse(bitis) - 60_000 > simdi.getTime();
}

export function bitisHesapla(simdi: Date, saniye: unknown): string {
  const s = typeof saniye === "number" && saniye > 0 ? saniye : 3600;
  return new Date(simdi.getTime() + s * 1000).toISOString();
}
